/**
 * The composer. The one thing it must never do is lose what somebody typed.
 *
 * On submit the text leaves the box so the optimistic bubble can take its place —
 * but if the post fails, the exact text goes straight back into the box, the box
 * gets focus, and the error says so plainly. Nobody types a note to their family,
 * watches it vanish, and has to guess whether it landed.
 */
import { useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { Identity } from '../../shared/types';
import { Avatar, Banner, btnPrimary, cx, textArea } from './shared';

/** Matches MAX_BODY in worker/routes/messages.ts. The Worker is what enforces it. */
const MAX_BODY = 2000;

export default function Composer({
  me,
  onPost,
  rows = 2,
  placeholder = 'Leave a note for the family…',
  autoFocus = false,
}: {
  me: Identity;
  /** Throws (with a human-readable message) if the message did not save. */
  onPost: (body: string) => Promise<unknown>;
  rows?: number;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const fieldId = useId();

  const trimmed = draft.trim();
  const tooLong = trimmed.length > MAX_BODY;
  const canSend = trimmed.length > 0 && !tooLong && !busy;

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSend) {
      if (!trimmed) setError('Write something first.');
      return;
    }

    const text = trimmed;
    setDraft(''); // the optimistic bubble is now holding these words
    setBusy(true);
    setError(null);
    setPosted(false);

    try {
      await onPost(text);
      setPosted(true);
      window.setTimeout(() => setPosted(false), 2500);
    } catch (err) {
      // Put it back, exactly as typed, and say what happened.
      setDraft(text);
      setError(
        `${err instanceof Error ? err.message : 'Your message didn’t post.'} Your words are back in the box — try again.`
      );
      boxRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Cmd/Ctrl + Enter posts. Plain Enter stays a newline — a kid typing a list
    // shouldn't fire off half a message.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex items-start gap-3">
        <Avatar name={me.name ?? 'You'} color={me.color} />
        <div className="min-w-0 flex-1">
          <label htmlFor={fieldId} className="sr-only">
            Write a message to the family
          </label>
          <textarea
            id={fieldId}
            ref={boxRef}
            rows={rows}
            value={draft}
            autoFocus={autoFocus}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-describedby={tooLong ? `${fieldId}-count` : undefined}
            aria-invalid={tooLong || undefined}
            className={cx(textArea, tooLong && 'border-over')}
          />
        </div>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {posted && !error && <Banner tone="ok">Posted to the board.</Banner>}

      <div className="flex items-center justify-between gap-3 pl-13">
        <p
          id={`${fieldId}-count`}
          className={cx(
            'text-xs',
            tooLong ? 'font-medium text-over' : 'text-ink-faint',
            !tooLong && trimmed.length < MAX_BODY - 200 && 'invisible'
          )}
        >
          {tooLong
            ? `${trimmed.length - MAX_BODY} characters too many`
            : `${MAX_BODY - trimmed.length} characters left`}
        </p>
        <button type="submit" disabled={!canSend} className={btnPrimary}>
          {busy ? 'Posting…' : 'Post'}
        </button>
      </div>
    </form>
  );
}
